/**
 * v3.15-1155 — Partner Presentation (dedicated public page).
 *
 * Elevates the existing PartnerPresentation modal content into a premium,
 * scrollable, full-page experience. Preserves all original content; expands
 * with Consumer Funding, Business Funding, and CreditForward360 sections
 * required by v3.15-1155.
 *
 * Existing PartnerPresentation modal component is left intact (per PRD)
 * but no public CTA opens it — all "Become a Partner" buttons now route here.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowDown, ShieldCheck, Eye, Banknote, Zap, Users, Building2, DollarSign, BadgeCheck, CheckCircle2 } from 'lucide-react';
import PublicLayout from '../components/PublicLayout';
import EcosystemGraphic from '../components/EcosystemGraphic';
import GhostLogo from '../components/GhostLogo';
import PartnerOnboarding from '../components/PartnerOnboarding';

const PresentationPage = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);

  return (
    <PublicLayout testId="presentation-page">
      {/* 1. Hero */}
      <section className="bg-primary text-white py-24 px-6" data-testid="presentation-hero">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-amber-400 text-sm font-medium uppercase tracking-[0.2em] mb-6">
            The Ultimate Partner Infrastructure
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl font-bold leading-tight mb-6">
            Run Your Business.<br />
            <span className="text-amber-400">Keep the Profit.</span>
          </h1>
          <p className="text-xl text-white/80 mb-10">We Power Everything Behind It.</p>
          <button
            onClick={() => setShowOnboarding(true)}
            data-testid="presentation-hero-apply-btn"
            className="inline-flex items-center gap-2 px-8 py-4 bg-amber-400 text-slate-900 rounded-lg hover:bg-amber-300 transition-all font-semibold text-lg"
          >
            Apply to Partner Network
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      {/* 2. Why Prestigious Hub */}
      <section className="bg-white py-20 px-6" data-testid="presentation-why">
        <div className="max-w-4xl mx-auto">
          <p className="text-slate-500 text-sm uppercase tracking-[0.2em] mb-4">Why partners choose us</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6 leading-tight">
            Our partners don&rsquo;t hire processors.
            <span className="text-primary"> They hire us.</span>
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5 text-base text-slate-700 leading-relaxed">
            <p><strong className="text-slate-900">Our Concierge handles the work.</strong> One named human per client, from submission to funding.</p>
            <p><strong className="text-slate-900">Our technology keeps everyone informed.</strong> The platform is the source of truth — no status calls.</p>
            <p><strong className="text-slate-900">Ghost keeps everything connected.</strong> Our AI operations assistant runs 24/7, even when your office is closed.</p>
            <p><strong className="text-slate-900">Our platform becomes an extension of your business.</strong> You close. We execute. You keep the relationship.</p>
          </div>
        </div>
      </section>

      {/* 2.5. What You Get — Platform pillars (restores Ghost per v3.15-1156). */}
      <section className="bg-slate-50 py-20 px-6" data-testid="presentation-what-you-get">
        <div className="max-w-5xl mx-auto">
          <p className="text-slate-500 text-sm uppercase tracking-[0.2em] mb-3 text-center">What You Get</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-3 text-center">
            One platform. One team. Three financial solutions.
          </h2>
          <p className="text-center text-slate-500 max-w-2xl mx-auto mb-10 text-sm">
            Consumer Funding (EasyQuickFund), Business Funding, and Credit Improvement (CreditForward360) — orchestrated by a human Application Concierge and accelerated by Ghost Intelligence™.
          </p>
          <div className="mb-10">
            <EcosystemGraphic testId="presentation-ecosystem-graphic" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="pillar-operational-engine">
              <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">Operational Engine — Built for Scale</h3>
              <p className="text-sm text-slate-600 leading-relaxed">A structured system that keeps deals moving, clients engaged, and workflows organized.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="pillar-concierge-shield">
              <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">The Concierge Shield — Full-Service Execution</h3>
              <p className="text-sm text-slate-600 leading-relaxed">Every deal managed from submission to funding. You close. We execute.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="pillar-client-command-center">
              <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">Client Command Center</h3>
              <p className="text-sm text-slate-600 leading-relaxed">Real-time visibility into every deal. If it&apos;s in the system — you see it.</p>
            </div>
            <Link to="/ghost" className="bg-white border-l-4 border-primary border-y border-r border-slate-200 rounded-lg p-6 hover:shadow-soft transition-all" data-testid="pillar-ghost">
              <div className="flex items-center gap-3 mb-2">
                <GhostLogo size="sm" />
                <h3 className="font-heading text-lg font-bold text-slate-900">Ghost<sup className="text-[9px]">™</sup> — Your AI Operations Assistant</h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">Monitors every file in real time, flags risk, drafts communication, and assists your Concierge 24/7. <span className="text-primary font-medium">Learn more →</span></p>
            </Link>
          </div>
        </div>
      </section>

      {/* 3. Consumer Funding */}
      <section className="bg-slate-50 py-20 px-6" data-testid="presentation-consumer">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Consumer Funding through EasyQuickFund™</p>
            <h2 className="font-heading text-4xl font-bold text-slate-900 mb-4 leading-tight">
              Personal financing without the friction.
            </h2>
            <p className="text-base text-slate-700 leading-relaxed mb-4">
              <strong>EasyQuickFund™</strong> is the consumer funding brand inside the Prestigious Companies network — fast, flexible personal financing through our growing lender panel. Most options begin with a soft inquiry, so clients see realistic offers before any impact to their credit.
            </p>
            <ul className="space-y-2 mt-4">
              {[
                'Soft-pull prequalification',
                'No hard inquiry to explore options',
                'Financing up to $100,000',
                'Approximately 150 lending partners',
                'ACH-based payment processing',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-soft">
            <DollarSign className="h-10 w-10 text-primary mb-4" />
            <p className="font-heading text-2xl font-bold text-slate-800 mb-2">~150 lenders. One decision.</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              You are not limited to a single lender. If one says no, others are still evaluating the deal. Your Concierge keeps the file moving until every realistic option is exhausted.
            </p>
          </div>
        </div>
      </section>

      {/* 4. Business Funding */}
      <section className="bg-white py-20 px-6" data-testid="presentation-business">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Business Funding · Commercial Capital</p>
          <h2 className="font-heading text-4xl font-bold text-slate-900 mb-4 leading-tight">
            One application. One Concierge. One platform.<br />
            <span className="text-primary">Multiple funding solutions.</span>
          </h2>
          <p className="text-base text-slate-700 leading-relaxed mb-10 max-w-3xl">
            Your business clients get the same Concierge experience as our consumer flow — one point of contact from submission to funding, with the full menu of commercial capital products available through the Hub.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { t: 'Working Capital',         b: 'Short-term liquidity to keep operations moving.' },
              { t: 'Business Term Loans',     b: 'Structured installment financing for growth.' },
              { t: 'Equipment Financing',     b: 'Acquire or replace equipment without depleting reserves.' },
              { t: 'Lines of Credit',         b: 'Flexible draw access for cash-flow timing gaps.' },
              { t: 'Revenue-Based Financing', b: 'Capital that flexes with monthly receipts.' },
              { t: 'SBA Options',             b: 'Government-backed programs for qualifying businesses.' },
            ].map((p) => (
              <div key={p.t} className="bg-slate-50 border border-slate-200 rounded p-4">
                <Building2 className="h-4 w-4 text-primary mb-2" />
                <p className="font-heading text-sm font-bold text-slate-900 mb-1">{p.t}</p>
                <p className="text-xs text-slate-600 leading-relaxed">{p.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. CreditForward360 */}
      <section className="bg-slate-50 py-20 px-6" data-testid="presentation-cf360">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Credit Improvement · CreditForward360</p>
          <h2 className="font-heading text-4xl font-bold text-slate-900 mb-4">
            Today&rsquo;s decline becomes tomorrow&rsquo;s approval.
          </h2>
          <p className="text-base text-slate-700 leading-relaxed max-w-2xl mx-auto">
            <strong>CreditForward360 helps clients who aren&rsquo;t fundable today become fundable tomorrow</strong> — through personalized credit improvement plans designed specifically for future financing. When a client cannot be funded right away, the Concierge routes them into CreditForward360 instead of letting them walk away. A &ldquo;not yet&rdquo; never becomes a permanent &ldquo;no.&rdquo;
          </p>
        </div>
      </section>

      {/* 6. Application Concierge */}
      <section className="bg-primary text-white py-24 px-6" data-testid="presentation-concierge">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-3">Our Largest Competitive Advantage</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mb-6 leading-tight">
              The Concierge Shield
            </h2>
            <p className="text-base text-slate-200 leading-relaxed mb-4">
              Every client we receive is assigned one dedicated Application
              Concierge who personally guides them from the moment the file opens
              through the day funds arrive.
            </p>
            <p className="text-base text-slate-200 leading-relaxed">
              Our concierge team becomes an extension of your business, allowing
              you to focus on growing your company while we guide every client
              through the funding process.
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              'Client communication',
              'Document collection',
              'Application review',
              'Lender coordination',
              'Funding coordination',
              'Status updates',
              'Client support',
              'End-to-end guidance',
            ].map((duty) => (
              <li key={duty} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded p-4">
                <CheckCircle2 className="h-4 w-4 text-emerald-300 mt-0.5 shrink-0" />
                <span className="text-sm text-slate-100">{duty}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 7. Lending Network + 8. Soft Pull */}
      <section className="bg-white py-20 px-6" data-testid="presentation-network">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <Users className="h-8 w-8 text-primary mb-3" />
            <p className="font-heading text-3xl font-bold text-primary leading-none">~150</p>
            <p className="font-heading text-base font-bold text-slate-800 mt-2">Lending Partners</p>
            <p className="text-sm text-slate-600 mt-2">A growing nationwide network — consumer, business and specialty programs.</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <BadgeCheck className="h-8 w-8 text-primary mb-3" />
            <p className="font-heading text-lg font-bold text-slate-800">Soft-Pull Pre-Approvals</p>
            <p className="text-sm text-slate-600 mt-2">Realistic options surfaced with zero impact to your client&apos;s credit score.</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <Eye className="h-8 w-8 text-primary mb-3" />
            <p className="font-heading text-lg font-bold text-slate-800">Real-Time Partner Transparency</p>
            <p className="text-sm text-slate-600 mt-2">Live deal status, lender activity, and document checklists — visible to you 24/7.</p>
          </div>
        </div>
      </section>

      {/* 9. Deal Protection */}
      <section className="bg-slate-50 py-20 px-6" data-testid="presentation-deal-protection">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Proven process. Deal protection.
          </h2>
          <p className="text-base text-slate-700 mb-6">
            With over <span className="font-semibold text-primary">20 years of experience</span> across multiple industries,
            we understand how to structure deals the right way.
          </p>
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-soft">
            <p className="text-slate-800 mb-1">Deals we are involved in have not experienced cancellations.</p>
            <p className="font-heading text-3xl font-bold text-primary">Zero. Not one.</p>
          </div>
        </div>
      </section>

      {/* 10. Why Partners Choose Us */}
      <section className="bg-white py-20 px-6" data-testid="presentation-why-choose">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-3 text-center">
            Why partners choose us
          </h2>
          <p className="text-center text-slate-500 mb-10">Close more deals because you can say YES more often.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              ['Dedicated Application Concierge', Users],
              ['~150 Lending Partners', Building2],
              ['Consumer Funding', DollarSign],
              ['Business Funding', BadgeCheck],
              ['CreditForward360', ShieldCheck],
              ['Soft Pull Pre-Approvals', CheckCircle2],
              ['Real-Time Deal Tracking', Eye],
              ['ACH Processing', Banknote],
              ['Same-Day Wire Capability', Zap],
            ].map(([title, Icon]) => (
              <div key={title} className="bg-slate-50 border border-slate-100 rounded-lg p-5">
                <Icon className="h-5 w-5 text-primary mb-2" />
                <p className="text-sm font-semibold text-slate-800">{title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11. Standard Investment */}
      <section className="bg-slate-50 py-20 px-6" data-testid="presentation-pricing">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-3 text-center">Standard Investment</h2>
          <p className="text-center text-slate-500 mb-10">We only participate when you win.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 rounded-lg p-6 text-center" data-testid="pricing-activation">
              <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">One-Time</p>
              <p className="font-heading text-4xl font-bold text-slate-900">$495</p>
              <p className="text-slate-600 text-sm mt-1">Activation Fee</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6 text-center" data-testid="pricing-monthly">
              <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Monthly</p>
              <p className="font-heading text-4xl font-bold text-slate-900">$199</p>
              <p className="text-slate-600 text-sm mt-1">Partner License Fee</p>
            </div>
            <div className="bg-primary rounded-lg p-6 text-center" data-testid="pricing-success">
              <p className="text-amber-400 text-xs uppercase tracking-wider mb-1">Success-Based</p>
              <p className="font-heading text-4xl font-bold text-white">15%</p>
              <p className="text-white/80 text-sm mt-1">Per Funded Deal</p>
            </div>
          </div>
        </div>
      </section>

      {/* 11b. Sign Up Today — Promotional Pricing */}
      <section className="bg-slate-50 py-20 px-6" data-testid="presentation-pricing-promo">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-3 text-center">Sign Up Today</h2>
          <p className="text-center text-slate-500 mb-10">Enroll today and take advantage of our current promotional pricing.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 rounded-lg p-6 text-center" data-testid="pricing-promo-activation">
              <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">One-Time</p>
              <p className="font-heading text-4xl font-bold text-slate-900">$199</p>
              <p className="text-slate-600 text-sm mt-1">Activation Fee</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6 text-center" data-testid="pricing-promo-monthly">
              <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Monthly</p>
              <p className="font-heading text-4xl font-bold text-slate-900">$99</p>
              <p className="text-slate-600 text-sm mt-1">Partner License Fee</p>
            </div>
            <div className="bg-primary rounded-lg p-6 text-center" data-testid="pricing-promo-success">
              <p className="text-amber-400 text-xs uppercase tracking-wider mb-1">Success-Based</p>
              <p className="font-heading text-4xl font-bold text-white">10%</p>
              <p className="text-white/80 text-sm mt-1">Per Funded Deal</p>
            </div>
          </div>
        </div>
      </section>

      {/* 12. Final CTA (post-promo mirror — same design as end-of-page CTA) */}
      <section className="bg-primary text-white py-24 px-6 text-center" data-testid="presentation-cta-promo">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold mb-4">Everything you need is already built.</h2>
          <p className="text-xl text-white/80 mb-10">You bring the deals. The system handles the rest.</p>
          <button
            onClick={() => setShowOnboarding(true)}
            data-testid="presentation-promo-apply-btn"
            className="inline-flex items-center gap-2 px-8 py-4 bg-amber-400 text-slate-900 rounded-lg hover:bg-amber-300 transition-all font-semibold text-lg"
          >
            Apply to Partner Network
            <ChevronRight className="h-5 w-5" />
          </button>
          <p className="text-amber-400/80 text-sm mt-4">Approvals are limited and processed within 24–48 hours.</p>
        </div>
      </section>

      {/* 13. Final CTA */}
      <section className="bg-primary text-white py-24 px-6 text-center" data-testid="presentation-cta">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold mb-4">Everything you need is already built.</h2>
          <p className="text-xl text-white/80 mb-10">You bring the deals. The system handles the rest.</p>
          <button
            onClick={() => setShowOnboarding(true)}
            data-testid="presentation-final-apply-btn"
            className="inline-flex items-center gap-2 px-8 py-4 bg-amber-400 text-slate-900 rounded-lg hover:bg-amber-300 transition-all font-semibold text-lg"
          >
            Apply to Partner Network
            <ChevronRight className="h-5 w-5" />
          </button>
          <p className="text-amber-400/80 text-sm mt-4">Approvals are limited and processed within 24–48 hours.</p>
        </div>
      </section>

      <PartnerOnboarding isOpen={showOnboarding} onClose={() => setShowOnboarding(false)} />
    </PublicLayout>
  );
};

export default PresentationPage;
